<template>
  <v-container
    class="cf-container rounded elevation-3"
    fluid>
    <!-- Header -->
    <div class="d-flex align-start justify-space-between mb-4">
      <div>
        <div class="d-flex align-center">
          <v-avatar
            size="30"
            tile
            class="cf-title-icon mr-2">
            <v-icon
              size="18"
              color="amber darken-2">
              mdi-form-textbox
            </v-icon>
          </v-avatar>
          <h2 class="mb-0">
            Custom Field
          </h2>
        </div>
        <p class="cf-subtitle mb-0 mt-1">
          ฟิลด์เสริมสำหรับจดโน้ต / เก็บข้อมูลในหน้า Time and Action
        </p>
      </div>
    </div>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="customFields"
      :loading="loading"
      :items-per-page="-1"
      sort-by="name"
      hide-default-footer
      class="elevation-0 cf-table"
    >
      <template #[`item.name`]="{ item }">
        <code class="cf-name">{{ item.name }}</code>
      </template>
      <template #[`item.label`]="{ item }">
        <span class="font-weight-medium">{{ item.label }}</span>
      </template>
      <template #[`item.status`]="{ item }">
        <v-icon
          large
          class="mr-3"
          :color="item.status === 'active' ? 'success' : 'grey'"
          :title="item.status === 'active' ? 'ปิดใช้งาน' : 'เปิดใช้งาน'"
          :disabled="togglingId === item.id"
          @click="toggleStatus(item)"
        >
          {{ item.status === 'active' ? 'mdi-toggle-switch' : 'mdi-toggle-switch-off-outline' }}
        </v-icon>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          small
          color="primary"
          class="mr-3"
          @click="openEditDialog(item)"
        >
          mdi-pencil
        </v-icon>
      </template>
      <template #no-data>
        <div class="py-6 grey--text">
          ยังไม่มี Custom Field
        </div>
      </template>
    </v-data-table>

    <!-- Create / Edit dialog -->
    <v-dialog
      v-model="dialog"
      max-width="440"
    >
      <v-card>
        <v-card-title class="d-flex align-center py-3">
          <v-icon
            color="amber darken-2"
            class="mr-2">
            mdi-form-textbox
          </v-icon>
          <span class="text-h6">{{ isEditMode ? 'แก้ไข Custom Field' : 'เพิ่ม Custom Field' }}</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form
            ref="fieldForm"
            v-model="formValid"
          >
            <p class="cf-field-label">
              Name <span class="cf-hint">(key ที่ใช้อ้างอิง เช่น custom1)</span>
            </p>
            <v-text-field
              v-model="form.name"
              placeholder="เช่น custom1"
              :disabled="isEditMode"
              outlined
              dense
              autofocus
              :rules="[
                v => !!(v && v.trim()) || 'กรุณาระบุ name',
                v => !/\s/.test(v || '') || 'name ห้ามมีช่องว่าง'
              ]"
            />

            <p class="cf-field-label">
              Label <span class="cf-hint">(ชื่อที่แสดงในหน้า Time and Action)</span>
            </p>
            <v-text-field
              v-model="form.label"
              placeholder="เช่น Product Name"
              outlined
              dense
              :rules="[v => !!v || 'กรุณาระบุ label']"
              @keyup.enter="saveField"
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="saving"
            @click="dialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="!formValid || saving"
            @click="saveField">
            {{ isEditMode ? 'Update' : 'Create' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'

const ProductionPlanService = new ProductionPlanProvider()

export default {
  data () {
    return {
      productionPlanService: ProductionPlanService,
      loading: false,
      saving: false,
      togglingId: null,
      customFields: [],
      headers: [
        {
          text: 'Name',
          value: 'name',
          align: 'start',
          sort: (a, b) => (a || '').localeCompare(b || '', undefined, { numeric: true })
        },
        { text: 'Label', value: 'label', align: 'start' },
        { text: 'Inactive/Active', value: 'status', align: 'center' },
        {
          text: 'Actions', value: 'actions', align: 'end', sortable: false
        }
      ],
      dialog: false,
      formValid: false,
      editingId: null,
      form: {
        name: '',
        label: ''
      }
    }
  },
  computed: {
    isEditMode () {
      return !!this.editingId
    }
  },
  mounted () {
    this.fetchCustomFields()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setModal: 'Components/setModal'
    }),
    async fetchCustomFields () {
      this.loading = true
      try {
        const { data } = await this.productionPlanService.getCustomFields()
        this.customFields = Array.isArray(data) ? data : (data?.results ?? [])
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: `ERROR WHILE LOAD CUSTOM FIELDS: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    resetForm () {
      this.form = {
        name: '',
        label: ''
      }
      this.editingId = null
      if (this.$refs.fieldForm) this.$refs.fieldForm.resetValidation()
    },
    openCreateDialog () {
      this.resetForm()
      this.dialog = true
    },
    openEditDialog (item) {
      this.editingId = item.id
      this.form = {
        name: item.name,
        label: item.label
      }
      this.dialog = true
    },
    async saveField () {
      if (!this.$refs.fieldForm.validate()) return

      this.saving = true
      try {
        const payload = {
          name: this.form.name.replace(/\s+/g, ''),
          label: this.form.label
        }

        if (this.isEditMode) {
          await this.productionPlanService.updateCustomField(this.editingId, payload)
        } else {
          await this.productionPlanService.createCustomField(payload)
        }

        this.setSnackbar({
          value: true,
          message: this.isEditMode ? 'Custom Field Updated' : 'Custom Field Created',
          type: 'success'
        })
        this.dialog = false
        await this.fetchCustomFields()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'บันทึกข้อมูลผิดพลาด',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    async toggleStatus (item) {
      const nextStatus = item.status === 'active' ? 'inactive' : 'active'
      this.togglingId = item.id
      try {
        await this.productionPlanService.updateCustomField(item.id, {
          name: item.name,
          label: item.label,
          status: nextStatus
        })
        this.setSnackbar({
          value: true,
          message: nextStatus === 'active' ? 'เปิดใช้งานแล้ว' : 'ปิดใช้งานแล้ว',
          type: 'success'
        })
        await this.fetchCustomFields()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'เปลี่ยนสถานะผิดพลาด',
          type: 'error'
        })
      } finally {
        this.togglingId = null
      }
    }
  }
}
</script>

<style scoped>
.cf-container {
  background: #fff;
  padding: 20px 24px;
}

.cf-title-icon {
  background: #fff8e1 !important;
  border-radius: 8px !important;
}

.cf-subtitle {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.5);
}

.cf-table ::v-deep thead th {
  white-space: nowrap;
}

.cf-field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}

.cf-hint {
  color: rgba(0, 0, 0, 0.4);
  font-weight: 400;
}

.cf-name {
  background: #f2f4f5;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  color: #5f5f5f;
}
</style>
